import React, { useRef, useState, Suspense } from "react";
import { useFrame } from "@react-three/fiber";
import { AdaptiveDpr, Preload, Environment as DreiEnv, Lightformer } from "@react-three/drei";
import { XR, XROrigin, TeleportTarget } from "@react-three/xr";
import * as THREE from "three";
import { xrStore } from "./xrStore";
import Environment from "./Environment";
import Plaza from "./Plaza";
import Water from "./Water";
import City from "./City";
import Skyline from "./Skyline";
import Traffic from "./Traffic";
import Bridges from "./Bridges";
import StatueOfLiberty from "./StatueOfLiberty";
import AmbientLife from "./AmbientLife";
import Interiors from "./Interiors";
import WorldFinancialCenter from "./WorldFinancialCenter";
import Woolworth from "./Woolworth";
import WTCComplex from "./WTCComplex";
import StreetTrees from "./StreetTrees";
import Pedestrians from "./Pedestrians";
import Roads from "./Roads";
import Shorelines from "./Shorelines";
import TwinTowers from "./TwinTowers";
import OneWTC from "./OneWTC";
import Weather from "./Weather";
import Rig from "./Rig";
import VRPanel from "./VRPanel";
import { useStore } from "../store";
import { updateCameraState } from "./cameraState";

function FpsMeter() {
  const acc = useRef({ t: 0, n: 0 });
  useFrame((state, delta) => {
    acc.current.t += delta;
    acc.current.n += 1;
    updateCameraState(state.camera);
    if (acc.current.t >= 0.5) {
      const fps = Math.round(acc.current.n / acc.current.t);
      const s = useStore.getState();
      s.setFps(fps);
      // Auto-scaling LOD: chase ~90 FPS (VR) by trimming instanced blocks/NPCs.
      let q = s.qualityScale;
      if (fps < 45) q = Math.max(0.4, q - 0.1);
      else if (fps > 75) q = Math.min(1, q + 0.08);
      if (Math.abs(q - s.qualityScale) > 0.001) s.setQualityScale(q);
      acc.current.t = 0;
      acc.current.n = 0;
    }
  });
  return null;
}

function Timelapse() {
  useFrame((_, delta) => {
    const s = useStore.getState();
    if (s.timelapse) {
      let v = s.temporal + delta * 2.2;
      if (v >= 2026) v = 2001;
      s.setTemporal(v);
    }
  });
  return null;
}

export default function Scene() {
  const [teleport, setTeleport] = useState(() => new THREE.Vector3(-8, 0, 130));
  const vrActive = useStore((s) => s.vrActive);

  return (
    <>
      <AdaptiveDpr pixelated />
      <XR store={xrStore}>
        <fog attach="fog" args={["#cfe3ef", 800, 6500]} />
        <Suspense fallback={null}>
          <Environment />
          <Skyline />
          <Water />
          <Plaza />
          <City />
          <WorldFinancialCenter />
          <Woolworth />
          <WTCComplex />
          <Shorelines />
          <StreetTrees />
          <Traffic />
          <Roads />
          <Pedestrians />
          <Bridges />
          <AmbientLife />
          <StatueOfLiberty />
          <TwinTowers />
          <OneWTC />
          <Interiors />
          <Weather />
        </Suspense>

        <Rig />
        <FpsMeter />
        <Timelapse />

        {vrActive && (
          <>
            <XROrigin position={teleport} />
            <TeleportTarget onTeleport={setTeleport}>
              <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]}>
                <planeGeometry args={[380, 420]} />
                <meshStandardMaterial transparent opacity={0} />
              </mesh>
            </TeleportTarget>
            <VRPanel />
          </>
        )}
      </XR>
    </>
  );
}
